import React, {useEffect, useRef, useState} from 'react';
import {CheckCircleFilled, CloseCircleFilled, EditOutlined} from '@ant-design/icons';
import {Button, Card, Col, Row, Typography} from 'antd';

import ErrorBoundary from '../../../components/ErrorBoundary';
import LoginDetailsEdit from '../../staff/accounts/LoginEdit';
import {messageType, showMessages} from '../../../helpers/message';
import {getUser, sendLoginEmailVerification} from '../../../helpers/user';
import {errorRed, successGreen} from '../../../assets/colors';
import {useAppSelector} from '../../../hooks/useStore';

import {dataColSize, labelColSize} from '../../../assets/styles';

const {Text} = Typography;
const {Meta} = Card;

const unverified = <span style={{'color': errorRed}}><CloseCircleFilled/>Not Verified</span>;
const verified = <span style={{'color': successGreen}}><CheckCircleFilled/>Verified</span>;

const LoginDetailsStaff = () => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const user = useAppSelector(state => state.user.user);

    const [email, setEmail] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [verifiedAt, setVerifiedAt] = useState(null);

    const fetchUser = React.useCallback(async () => {
        const abortSignal = abortSignalRef.current;
        if (user?.id) {
            try {
                setLoading(true);
                const response = await getUser(user.id, abortSignal);
                if (response) {
                    setEmail(response.email);
                    setVerifiedAt(response.email_verified_at);
                }
            } catch (error) {
                showMessages(error.message, messageType.error);
                if (error.status_code === 404) window.location.replace('/404');
            } finally {
                setLoading(false);
            }
        }
    }, [user]);

    const resendVerification = async () => {
        const abortSignal = abortSignalRef.current;
        if (verifiedAt) {
            let message = 'This email has already been verified.';
            showMessages(message, messageType.error);
            return;
        }

        setLoading(true);

        try {
            let response = await sendLoginEmailVerification(false, abortSignal);
            if (response) {
                let message = 'Verification email has successfully been sent.';
                showMessages(message, messageType.success);
            }

            setLoading(false);
        } catch (error) {
            showMessages(error.message, messageType.error);
            if (error.message === 'Email already verified.') {
                setVerifiedAt(true);
            }
            setLoading(false);
        }
    };

    const onEdit = () => {
        toggleEditState(true);
    };

    const toggleEditState = (state) => {
        setIsEditing(state);
    };

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    useEffect(() => {
        const abortController = abortControllerRef.current;

        if (!user) {
            showMessages('This account has no associated user login.', messageType.warn);
        }

        return () => {
            abortController.abort();
        };
    }, [user]);

    return (
        <ErrorBoundary>
            <Card
                title="Login Details"
                bordered={true}
                loading={loading}
                className="details-card card-min-height-sm"
                extra={
                    user? <EditOutlined key="edit" onClick={onEdit}/> : null
                }
            >
                <Meta
                    className="card-content-min-height-sm"
                    description={
                        <>
                            <Row>
                                <Col {...labelColSize}>
                                    <Text strong>Email Address:</Text>
                                </Col>
                                <Col {...dataColSize}>
                                    <span className="long-text-wrap">{email || 'N/A'}</span>
                                </Col>
                            </Row>
                            <Row>
                                <Col {...labelColSize}>
                                    <Text strong>Verification Status:</Text>
                                </Col>
                                <Col {...dataColSize}>
                                    {verifiedAt === null ? unverified : verified}
                                </Col>
                            </Row>
                        </>
                    }
                />
                <Button type="primary" onClick={resendVerification} disabled={verifiedAt || !user}>
                    Resend Verification Email
                </Button>
            </Card>
            {
                isEditing &&
                <LoginDetailsEdit
                    email={email}
                    visible={isEditing}
                    toggleEditState={toggleEditState}
                    refreshLoginDetails={fetchUser}
                />
            }
        </ErrorBoundary>
    );
}

LoginDetailsStaff.propTypes = {};

export default LoginDetailsStaff;
